import { PayloadAction } from '@reduxjs/toolkit';
import { WritableDraft } from 'immer';

import { QuoteBuilderStateType } from '../../types/state';
import {
  getQuoteLineOrThrow,
  getQuotePriceOrThrow,
} from '../utilities/action-guards';
import { deleteIssue } from '../utilities/issue-managers';
import { withSaveStateUpdate } from '../utilities/with-save-state-update';
import { buildQuotePriceRejectedIssueId } from './quote-price-created';
import { QuotePriceParams } from './types';

// @TODO(#1684) - For now, we're keeping the deleted quote price until it is confirmed by the API
// We should update the UI to hide the quote price while it is being deleted.
// If deletion fails, we should keep the quote price hidden, but in the state, allowing the user
// to attempt re-deletion.

export function quotePriceDeleted(
  state: WritableDraft<QuoteBuilderStateType>,
  action: PayloadAction<QuotePriceParams>,
) {
  const { quoteLineUuid, quotePriceUuid } = action.payload;

  const quotePrice = getQuotePriceOrThrow(state, quoteLineUuid, quotePriceUuid);

  quotePrice.status = 'deleting';
}

function quotePriceDeletionSucceededBase(
  state: WritableDraft<QuoteBuilderStateType>,
  action: PayloadAction<{ quoteLineUuid: string; quotePriceUuid: string }>,
) {
  const { quoteLineUuid, quotePriceUuid } = action.payload;

  Object.entries(state.issues).forEach(([issueId, issue]) => {
    if (
      issue.area === 'quotePrice' &&
      issue.quotePriceUuid === quotePriceUuid
    ) {
      deleteIssue(state, issueId);
    }
  });

  const quoteLine = getQuoteLineOrThrow(state, quoteLineUuid);

  quoteLine.quotePriceUuids = quoteLine.quotePriceUuids.filter(
    (id) => id !== quotePriceUuid,
  );

  delete quoteLine.quotePrices[quotePriceUuid];
}

export const quotePriceDeletionSucceeded = withSaveStateUpdate(
  quotePriceDeletionSucceededBase,
);

/**
 * Gives up on a deletion the API refused, rolling the optimistic delete back.
 *
 * The price is still in Postgres, so the row goes back to `ready` rather than
 * being dropped from the store the way a discarded creation is - dropping it
 * would only hide a price that returns on the next load.
 */
export function quotePriceDeletionAbandoned(
  state: WritableDraft<QuoteBuilderStateType>,
  action: PayloadAction<QuotePriceParams>,
) {
  const { quoteLineUuid, quotePriceUuid } = action.payload;

  const quotePrice = getQuotePriceOrThrow(state, quoteLineUuid, quotePriceUuid);

  quotePrice.status = 'ready';

  deleteIssue(
    state,
    buildQuotePriceRejectedIssueId({ quoteLineUuid, quotePriceUuid }),
  );
}
